import React, { useEffect, useCallback, useState } from 'react';
import { Button, Card, Form, Radio, Spin, Space, Pagination } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { getClient, getClients, getMembers } from 'src/api/api';
import { useAppSelector, useAppDispatch } from 'src/redux/hooks';
import { updateClientInfo, updateSelectedClient, updateSelectedClientAdmins } from 'src/redux/slices/clientSlice';
import './style.css';
import getPermissions from 'src/utils/getPermissions';
import { updateTenantAdmins } from 'src/redux/slices/tenantSlice';
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_OFFSET } from 'src/utils/constants';

/**
 * Client selection page component that allows users to browse and select from available clients.
 * This component handles client listing with pagination, client selection, and admin permissions.
 * 
 * @component
 * @returns {JSX.Element} The client selection page with form and pagination
 */
const Client: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(state => state.auth.accessToken);
  const clientsInfo = useAppSelector(state => state.client);
  const user = useAppSelector(state => state.user);
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [fetchClientsParams, setFetchClientsParams] = useState<API.FetchParams>({
    limit: DEFAULT_PAGE_SIZE,
    offset: DEFAULT_PAGE_OFFSET,
  })

  /**
   * Fetches the list of available clients from the API with pagination support.
   * Updates the Redux store with client information and manages loading state.
   * 
   * @async
   * @function fetchClients
   * @returns {Promise<void>} Promise that resolves when clients are fetched and stored
   * 
   * @throws {Error} Logs error to console if API request fails
   */
  const fetchClients = useCallback(async () => {
    try {
      setLoadingMessage(t('Loading clients'))
      const response = await getClients(accessToken, fetchClientsParams);
      dispatch(updateClientInfo(response.data));
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
    finally {
      setLoadingMessage('')
    }
  }, [accessToken, dispatch, fetchClientsParams, t]);

  // Effect to fetch clients when component mounts or parameters change
  useEffect(() => {
    fetchClients();
  }, [fetchClients, fetchClientsParams]);

  /**
   * Handles pagination changes by updating fetch parameters and current page.
   * Calculates the correct offset based on page number and page size.
   * 
   * @function handlePageChange
   * @param {number} page - The new page number (1-based)
   * @param {number} pageSize - Number of items per page
   */
  const handlePageChange = (page: number, pageSize: number) => {
    setFetchClientsParams((prev) => ({
      ...prev,
      limit: pageSize,
      offset: (page - 1) * pageSize,
    }));
    setCurrentPage(page);
  };

  /**
   * Handles client selection from the form submission.
   * Fetches detailed client information, sets up admin permissions if applicable,
   * and navigates to the expenses page.
   * 
   * @async
   * @function selectClient
   * @param {Object} values - Form values containing the selected client
   * @param {string} values.client - The ID of the selected client
   * @returns {Promise<void>} Promise that resolves when client selection is complete
   * 
   * @throws {Error} Logs error to console if client selection fails
   */
  const selectClient = async (values: { client: string }) => {
    setLoadingMessage(t('Setting client'))
    try {
      const newClientInfo = await getClient(accessToken, values.client);
      dispatch(updateSelectedClient(newClientInfo.data));
      
      // Get clientAdmins and save it in redux if client or tenant admin
      const permissions = getPermissions(user.permissions.groups, values.client, user.tenant.id);
      if(permissions.isClientAdmin || permissions.isTenantAdmin){
        const clientAdminGroupId = `user.expense.admin@${newClientInfo.data.id}.${user.tenant.id}`;
        const clientAdmin = await getMembers(clientAdminGroupId, accessToken);
        dispatch(updateSelectedClientAdmins(clientAdmin.data));
        
        const tenantAdminGroupId = `user.expense.admin@default.${user.tenant.id}`;
        const tenantAdmin = await getMembers(tenantAdminGroupId, accessToken);
        dispatch(updateTenantAdmins(tenantAdmin.data));
      }
      navigate('/my-expenses');
    }
    catch (error: any) {
        console.error(error);
    }
    finally{
      setLoadingMessage('')
    }
  }

  return (
    <Card
      className="aex-client-form"
      title={<span className='aider-exp-title aider-exp-login-title'>{t('Select client')}</span>}
    >
      <Form onFinish={selectClient}>
        <Form.Item
          name="client"
          rules={[{ required: true, message: t('Please select a client') }]}
          className="client-list-container"
        >
          <Spin tip={loadingMessage} spinning={loadingMessage !== ''} style={{ marginTop: '10px' }}>
            <Radio.Group>
              <Space direction="vertical">
                {clientsInfo.clientOptions.map(({ label, value }) => (
                  <Radio value={value} key={value}>
                    {label}
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
            {clientsInfo.totalClients > (fetchClientsParams.limit ?? DEFAULT_PAGE_SIZE) && <Pagination
            align="center"
            current={currentPage}
            onChange={handlePageChange}
            pageSize={fetchClientsParams.limit ?? DEFAULT_PAGE_SIZE}
            total={clientsInfo.totalClients}
            />}
          </Spin>
        </Form.Item>
        <div className="client-select-btn">
          <Button type="primary" htmlType="submit">
            {t('Select')}
          </Button>
        </div>
      </Form>
    </Card>
  );
};

export default Client;
